// MCHD Room Scheduler - Dynamic Refresh Button Component
// A reusable refresh button with loading states and animations

import { Button, ButtonProps } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useState, useEffect } from 'react';

interface DynamicRefreshButtonProps extends Omit<ButtonProps, 'leftSection' | 'loading'> {
  onRefresh: () => void | Promise<void>;
  isLoading?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'filled' | 'light' | 'outline' | 'subtle' | 'default' | 'gradient';
  color?: string;
  children?: React.ReactNode;
  showText?: boolean;
  iconSize?: number;
}

/**
 * Dynamic refresh button with loading states and animations
 * @param props - Component props
 * @returns JSX element
 */
export function DynamicRefreshButton({
  onRefresh,
  isLoading = false,
  size = 'sm',
  variant = 'light',
  color = 'blue',
  children = 'Refresh',
  showText = true,
  iconSize = 16,
  ...buttonProps
}: DynamicRefreshButtonProps): JSX.Element {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [rotation, setRotation] = useState(0);

  // Handle refresh click
  const handleRefresh = async (): Promise<void> => {
    if (isRefreshing || isLoading) return;
    
    setIsRefreshing(true);
    
    try {
      await onRefresh();
    } finally {
      // Add a small delay to show the animation
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  // Enhanced animation effect with spring physics
  useEffect(() => {
    if (isRefreshing || isLoading) {
      let startTime: number;
      let animationId: number;
      
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        
        // Smooth continuous rotation with easing
        const progress = Math.min(elapsed / 2000, 1); // 2 second duration
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const rotationValue = easeOutCubic * 720; // 2 full rotations
        
        setRotation(rotationValue);
        
        if (progress < 1) {
          animationId = requestAnimationFrame(animate);
        }
      };
      
      animationId = requestAnimationFrame(animate);
      
      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    } else {
      // Smooth return to original position with spring
      let startRotation = rotation;
      let startTime: number;
      let animationId: number;
      
      const returnToZero = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const duration = 400; // 400ms return animation
        const progress = Math.min(elapsed / duration, 1);
        
        // Spring easing for natural return
        const springEase = 1 - Math.pow(1 - progress, 4);
        const currentRotation = startRotation * (1 - springEase);
        
        setRotation(currentRotation);
        
        if (progress < 1) {
          animationId = requestAnimationFrame(returnToZero);
        }
      };
      
      animationId = requestAnimationFrame(returnToZero);
      
      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [isRefreshing, isLoading, rotation]);

  const isButtonLoading = isRefreshing || isLoading;

  return (
    <Button
      {...buttonProps}
      variant={variant}
      color={color}
      size={size}
      loading={isButtonLoading}
      leftSection={
        <IconRefresh
          size={iconSize}
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: isButtonLoading 
              ? 'transform 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
              : 'transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            filter: isButtonLoading 
              ? 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))' 
              : 'none',
          }}
        />
      }
      onClick={handleRefresh}
      disabled={isButtonLoading}
      style={{
        ...buttonProps.style,
        minWidth: 'auto', // Ensure consistent width calculation
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isButtonLoading ? 'scale(0.95)' : 'scale(1)',
        boxShadow: isButtonLoading 
          ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
          : '0 2px 4px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          transform: 'translateY(-1px) scale(1.02)',
          boxShadow: '0 6px 16px rgba(59, 130, 246, 0.25)',
        },
        '&:active': {
          transform: 'translateY(0) scale(0.98)',
          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
        },
      }}
      styles={{
        root: {
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px) scale(1.02)',
            boxShadow: '0 6px 16px rgba(59, 130, 246, 0.25)',
          },
          '&:active': {
            transform: 'translateY(0) scale(0.98)',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
          },
        },
      }}
    >
      {showText && children}
    </Button>
  );
}

export default DynamicRefreshButton;
